import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import './index.less';

import {
  AgentMessageEventChatCompressionDataInput,
  AgentMessageEventChatCompressionStatus,
  AgentMessageEventSummarizationActionActionTypeEnum,
} from '../../../../gamma-api';
import IbIcon from '../../common/IbIcon';
import IbTypography from '../../common/IbTypography';
import IbCollapse, { IbCollapsePanel } from '../../common/IbCollapse';
import MarkdownContent from '../../MarkdownContent';
import { agentMessageEventDataToMarkdown } from '../utils';
import Switcher from '../../common/Switcher';

const MAIN_CLASS_NAME = 'chat-compression-event-details';
const RAW_PANEL_CLASS_NAME = `${MAIN_CLASS_NAME}__raw-panel`;
const ERROR_PANEL_CLASS_NAME = `${MAIN_CLASS_NAME}__error-panel`;
const BEFORE_PANEL_CLASS_NAME = `${MAIN_CLASS_NAME}__before-panel`;
const AFTER_PANEL_CLASS_NAME = `${MAIN_CLASS_NAME}__after-panel`;

export enum DetailsRenderMode {
  RAW = 'Raw',
  STRUCTURED = 'Structured',
}

export interface IChatCompressionEventDetailsProps {
  chatCompressionData: AgentMessageEventChatCompressionDataInput;
}

const ChatCompressionEventDetails: React.FC<IChatCompressionEventDetailsProps> = ({ chatCompressionData }) => {
  const { t } = useTranslation();

  const [renderMode, setRenderMode] = useState(DetailsRenderMode.STRUCTURED);

  const onRenderModeChange = (value: string | boolean) => {
    setRenderMode(value as DetailsRenderMode);
  };

  const renderSummarizationDetails = () => (
    <>
      <IbCollapsePanel
        key="before"
        className={BEFORE_PANEL_CLASS_NAME}
        header={
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Chat compression event details: Before panel: Title')}
          </IbTypography.Paragraph>
        }
      >
        <MarkdownContent>
          {chatCompressionData.action.before.summary_content ||
            t('Agent: Chat compression event details: Empty summary')}
        </MarkdownContent>
      </IbCollapsePanel>
      <IbCollapsePanel
        key="after"
        className={AFTER_PANEL_CLASS_NAME}
        header={
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Chat compression event details: After panel: Title')}
          </IbTypography.Paragraph>
        }
      >
        <MarkdownContent>
          {chatCompressionData.action.after.summary_content ||
            t('Agent: Chat compression event details: Empty summary')}
        </MarkdownContent>
      </IbCollapsePanel>
    </>
  );

  const renderDetails = () => {
    if (renderMode === DetailsRenderMode.RAW) {
      return (
        <IbCollapsePanel
          key="raw"
          className={RAW_PANEL_CLASS_NAME}
          header={
            <IbTypography.Paragraph strong type="secondary">
              {t('Agent: Chat compression event details: Raw panel: Title')}
            </IbTypography.Paragraph>
          }
        >
          <MarkdownContent>{agentMessageEventDataToMarkdown(chatCompressionData)}</MarkdownContent>
        </IbCollapsePanel>
      );
    }

    if (chatCompressionData.status === AgentMessageEventChatCompressionStatus.Error) {
      return (
        <IbCollapsePanel
          key="error"
          className={ERROR_PANEL_CLASS_NAME}
          header={
            <IbTypography.Paragraph strong type="secondary">
              {t('Agent: Chat compression event details: Error panel: Title')}
            </IbTypography.Paragraph>
          }
        >
          <MarkdownContent>{chatCompressionData.error_message || ''}</MarkdownContent>
        </IbCollapsePanel>
      );
    }

    if (chatCompressionData.action.action_type === AgentMessageEventSummarizationActionActionTypeEnum.Summarization) {
      return renderSummarizationDetails();
    }

    throw new Error(`Unexpected chat compression action type: ${chatCompressionData.action.action_type}`);
  };

  return (
    <IbCollapse
      accordion={false}
      className={MAIN_CLASS_NAME}
      defaultActiveKey={['raw', 'error', 'before', 'after']}
      expandIcon={({ isActive }) => <IbIcon iconName={isActive ? 'down' : 'up'} size={20} />}
      expandIconPosition="right"
    >
      <Switcher
        options={[
          { value: DetailsRenderMode.RAW, label: <IbIcon iconName="code-brackets" size={16} /> },
          {
            value: DetailsRenderMode.STRUCTURED,
            label: <IbIcon iconName="list-view" size={16} />,
          },
        ]}
        value={renderMode}
        onChange={onRenderModeChange}
      />
      {renderDetails()}
    </IbCollapse>
  );
};

export default ChatCompressionEventDetails;
